import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { useNotification } from "../../hooks/use-notification";

const LeadCaptureModal = ({ isOpen, onClose }) => {
  const { showNotification } = useNotification();
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    mobileNumber: "",
    address: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  if (!isOpen) return null;

  const validateForm = () => {
    const newErrors = {};
    if (!formData.username.trim()) newErrors.username = "Name is required";
    if (!formData.email.trim()) newErrors.email = "Email is required";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      newErrors.email = "Invalid email format";
    if (!formData.mobileNumber.trim())
      newErrors.mobileNumber = "Mobile number is required";
    if (!/^\d{10}$/.test(formData.mobileNumber.replace(/\D/g, "")))
      newErrors.mobileNumber = "Invalid mobile number";
    if (!formData.address.trim()) newErrors.address = "Address is required";
    return newErrors;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(
        "https://plm-apis.vanixtechnologies.com/api/contact",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formData),
        },
      );

      const responseData = await response.json();
      const responseMessage =
        responseData?.message ||
        responseData?.description ||
        "Request processed successfully";

      if (response.ok) {
        showNotification(
          responseMessage ||
            "Farm assessment request submitted! Our team will reach out soon.",
          true,
        );

        setFormData({
          username: "",
          email: "",
          mobileNumber: "",
          address: "",
        });
        setErrors({});

        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        showNotification(
          responseMessage || "Something went wrong. Please try again.",
          false,
        );
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      showNotification(
        error.message ||
          "Unable to submit form. Please check your connection and try again.",
        false,
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AnimatePresence>
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
        data-testid="lead-capture-modal-overlay"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white rounded-2xl p-8 w-full max-w-md shadow-xl border border-black/10 relative"
          onClick={(e) => e.stopPropagation()}
          data-testid="lead-capture-modal"
        >
          <motion.button
            onClick={onClose}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 transition-colors"
            data-testid="modal-close-btn"
            type="button"
          >
            <X size={24} />
          </motion.button>

          <h2
            className="text-[24px] font-bold text-black"
            style={{ fontFamily: "Satoshi, sans-serif" }}
          >
            Farm Assessment
          </h2>
          <p
            className="text-[14px] text-gray-600 mt-2"
            style={{ fontFamily: "Satoshi, sans-serif" }}
          >
            Get a personalized assessment from our sales team
          </p>

          <form className="flex flex-col gap-4 mt-6" onSubmit={handleSubmit}>
            <div>
              <input
                type="text"
                name="username"
                placeholder="Your Full Name"
                value={formData.username}
                onChange={handleInputChange}
                className={`w-full border rounded-xl px-4 py-3 text-[16px] outline-none transition-colors ${
                  errors.username
                    ? "border-red-500 focus:border-red-500"
                    : "border-black/10 focus:border-[#00BD7E]"
                }`}
                style={{ fontFamily: "Satoshi, sans-serif" }}
                data-testid="modal-input-username"
                disabled={isLoading}
              />
              {errors.username && (
                <p className="text-red-500 text-[12px] mt-1">
                  {errors.username}
                </p>
              )}
            </div>

            <div>
              <input
                type="email"
                name="email"
                placeholder="Email Address"
                value={formData.email}
                onChange={handleInputChange}
                className={`w-full border rounded-xl px-4 py-3 text-[16px] outline-none transition-colors ${
                  errors.email
                    ? "border-red-500 focus:border-red-500"
                    : "border-black/10 focus:border-[#00BD7E]"
                }`}
                style={{ fontFamily: "Satoshi, sans-serif" }}
                data-testid="modal-input-email"
                disabled={isLoading}
              />
              {errors.email && (
                <p className="text-red-500 text-[12px] mt-1">{errors.email}</p>
              )}
            </div>

            <div>
              <input
                type="tel"
                name="mobileNumber"
                placeholder="Mobile Number (10 digits)"
                value={formData.mobileNumber}
                onChange={handleInputChange}
                className={`w-full border rounded-xl px-4 py-3 text-[16px] outline-none transition-colors ${
                  errors.mobileNumber
                    ? "border-red-500 focus:border-red-500"
                    : "border-black/10 focus:border-[#00BD7E]"
                }`}
                style={{ fontFamily: "Satoshi, sans-serif" }}
                data-testid="modal-input-mobile"
                disabled={isLoading}
              />
              {errors.mobileNumber && (
                <p className="text-red-500 text-[12px] mt-1">
                  {errors.mobileNumber}
                </p>
              )}
            </div>

            <div>
              <textarea
                name="address"
                placeholder="Farm Address / Location"
                value={formData.address}
                onChange={handleInputChange}
                rows="3"
                className={`w-full border rounded-xl px-4 py-3 text-[16px] outline-none transition-colors resize-none ${
                  errors.address
                    ? "border-red-500 focus:border-red-500"
                    : "border-black/10 focus:border-[#00BD7E]"
                }`}
                style={{ fontFamily: "Satoshi, sans-serif" }}
                data-testid="modal-input-address"
                disabled={isLoading}
              />
              {errors.address && (
                <p className="text-red-500 text-[12px] mt-1">
                  {errors.address}
                </p>
              )}
            </div>

            <motion.button
              type="submit"
              whileHover={{ scale: isLoading ? 1 : 1.05 }}
              whileTap={{ scale: isLoading ? 1 : 0.95 }}
              disabled={isLoading}
              className="bg-[#00BD7E] text-white text-[18px] font-medium rounded-xl px-6 py-3 w-full mt-2 hover:bg-[#00a86b] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ fontFamily: "Satoshi, sans-serif" }}
              data-testid="modal-submit-btn"
            >
              {isLoading ? "Submitting..." : "Submit Assessment Request"}
            </motion.button>
          </form>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default LeadCaptureModal;
